import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Plus, Plane, X, Users, UserPlus, UserMinus, Check, ArrowRight, ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import { HotelIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Flight, Hotel, DateRange } from "@/types/trip-types";
import { parseFlightNumber } from "@/utils/flight-utils";
import { TimePicker } from "@/components/ui/time-picker";
import Hey, { Trip as HeyTrip, Product } from "@/lib/hey";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { TripTimeline } from "./TripTimeline";
import { adjustHotelTimesBasedOnFlights } from "@/utils/hotelAdjustmentUtils";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import LocationFields from "@/components/profile/LocationFields";
import { getCountryCodeByName } from "@/utils/countryData";
import { useAnalytics } from "@/hooks/use-analytics";

interface TripDetailsDrawerProps {
  flights: Flight[];
  hotels: Hotel[];
  setFlights: (flights: Flight[]) => void;
  setHotels: (hotels: Hotel[]) => void;
  tripTitle: string;
  tripType: string;
  onTripTitleChange: (value: string) => void;
  onTripTypeChange: (value: string) => void;
}

type WorkflowStep = 'info' | 'flights' | 'hotels' | 'review';

interface FlightOption {
  flightNumber: string;
  departureIata: string;
  arrivalIata: string;
  departureDateTime: string;
  arrivalDateTime: string;
  departureAirportName: string;
  arrivalAirportName: string;
  departureCityName: string;
  arrivalCityName: string;
  departureTerminal?: string | null;
  arrivalTerminal?: string | null;
  carrierCode: string;
  flightNumberOnly: string;
  airline?: string | null;
}

export const TripDetailsDrawer = ({
  flights,
  hotels,
  setFlights,
  setHotels,
  tripTitle,
  tripType,
  onTripTitleChange,
  onTripTypeChange
}: TripDetailsDrawerProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { 
    trackEvent, 
    trackIndexAction,
    trackTimelineAction,
    trackError 
  } = useAnalytics();
  
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('info');
  const [workflowProgress, setWorkflowProgress] = useState(25);
  
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [travelers, setTravelers] = useState(1);
  const [skipFlightValidation, setSkipFlightValidation] = useState(true);
  const [isAddingFlight, setIsAddingFlight] = useState(false);
  const [flightOptions, setFlightOptions] = useState<FlightOption[]>([]);
  const [selectedFlightIndex, setSelectedFlightIndex] = useState<number | null>(null);
  const [showFlightDropdown, setShowFlightDropdown] = useState(false);
  
  const [isCreatingTrip, setIsCreatingTrip] = useState(false);
  
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const flightForm = useForm({
    defaultValues: {
      flightNumber: "",
      departureDate: today,
      departureTime: "08:00",
      arrivalTime: "11:00",
    }
  });
  
  const hotelForm = useForm({
    defaultValues: {
      name: "",
      city: "",
      country: "",
    }
  });
  
  const [newFlight, setNewFlight] = useState<Flight>({
    flightNumber: "",
    departureDate: today,
    arrivalDate: today,
    departureTime: "08:00",
    arrivalTime: "11:00",
    departureIata: "",
    arrivalIata: ""
  });
  
  const [newHotel, setNewHotel] = useState<Hotel>({
    name: "",
    city: "",
    country: "",
    checkIn: today,
    checkOut: tomorrow
  });
  
  const [calendarOpen, setCalendarOpen] = useState({
    flightDeparture: false,
    flightArrival: false,
    hotel: false
  });
  
  const [dateRange, setDateRange] = useState<DateRange>({
    from: today,
    to: tomorrow
  });

  useEffect(() => {
    switch(currentStep) {
      case 'info':
        setWorkflowProgress(25);
        trackEvent('NewTrip', 'workflow_step', 'info');
        break;
      case 'flights':
        setWorkflowProgress(50);
        trackEvent('NewTrip', 'workflow_step', 'flights');
        break;
      case 'hotels':
        setWorkflowProgress(75);
        trackEvent('NewTrip', 'workflow_step', 'hotels');
        break;
      case 'review':
        setWorkflowProgress(100);
        trackEvent('NewTrip', 'workflow_step', 'review');
        break;
    }
  }, [currentStep, setWorkflowProgress, trackEvent]);

  useEffect(() => {
    if (!skipFlightValidation) {
      toast({
        title: "Flight Validation Warning",
        description: "Please keep flight validation OFF for now."
      });
      setSkipFlightValidation(true);
    }
  }, [skipFlightValidation, toast]);

  const goToNextStep = () => {
    switch(currentStep) {
      case 'info':
        trackEvent('NewTrip', 'next_step', 'info_to_flights');
        setCurrentStep('flights');
        break;
      case 'flights':
        if (flights.length === 0) {
          trackEvent('NewTrip', 'validation_error', 'no_flights');
          toast({
            title: t("common.toast.error"),
            description: t("Please add at least one flight to continue"),
            variant: "destructive"
          });
          return;
        }
        trackEvent('NewTrip', 'next_step', 'flights_to_hotels');
        setCurrentStep('hotels');
        break;
      case 'hotels':
        trackEvent('NewTrip', 'next_step', 'hotels_to_review');
        setCurrentStep('review');
        break;
    }
  };
  
  const goToPreviousStep = () => {
    switch(currentStep) {
      case 'flights':
        trackEvent('NewTrip', 'previous_step', 'flights_to_info');
        setCurrentStep('info');
        break;
      case 'hotels':
        trackEvent('NewTrip', 'previous_step', 'hotels_to_flights');
        setCurrentStep('flights');
        break;
      case 'review':
        trackEvent('NewTrip', 'previous_step', 'review_to_hotels');
        setCurrentStep('hotels');
        break;
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      return format(date, "MMM d, yyyy HH:mm");
    } catch (e) {
      return dateTimeString;
    }
  };

  const selectFlightOption = (index: number) => {
    trackEvent('NewTrip', 'select_flight_option', `Option: ${index + 1}`);
    setSelectedFlightIndex(index);
    setShowFlightDropdown(false);
  };

  const handleAddFlight = async () => {
    if (!newFlight.flightNumber) {
      trackEvent('NewTrip', 'validation_error', 'empty_flight_number');
      toast({
        title: t("common.toast.error"),
        description: t("Please enter flight number"),
        variant: "destructive"
      });
      return;
    }
    
    setIsAddingFlight(true);
    trackEvent('NewTrip', 'add_flight_attempt', newFlight.flightNumber);
    
    try {
      const heyInstance = Hey.HeyUtils.getInstance();
      if (!heyInstance.isUserAuthenticated()) {
        toast({
          title: t("common.toast.error"),
          description: t("Please log in to add flights"),
          variant: "destructive"
        });
        return;
      }
      console.debug('🔍 Step 1: Input Flight Details:', {
        flightNumber: newFlight.flightNumber,
        departureDate: format(newFlight.departureDate, "yyyy-MM-dd"),
        departureTime: newFlight.departureTime,
        arrivalTime: newFlight.arrivalTime
      });
      const {
        carrierCode,
        flightNumber
      } = parseFlightNumber(newFlight.flightNumber);
      const departureDateFormatted = format(newFlight.departureDate, "yyyy-MM-dd");
      console.debug('🔍 Step 2: Parsed Flight Info:', {
        carrierCode,
        flightNumber,
        departureDateFormatted
      });
      console.debug('📡 Step 3: Calling Hey API getFlightDetails...');
      const flightDetails = await heyInstance.getFlightDetails(carrierCode, flightNumber, departureDateFormatted);
      console.debug('✨ Step 4: Raw Hey API Response:', JSON.stringify(flightDetails, null, 2));
      if (!flightDetails || !flightDetails.data || flightDetails.data.length === 0) {
        console.debug('❌ Step 5: No flight details found in response');
        toast({
          title: t("common.toast.error"),
          description: t("Flight not found or invalid flight number"),
          variant: "destructive"
        });
        return;
      }
      
      const options: FlightOption[] = flightDetails.data.map((flightInfo: any) => ({
        flightNumber: `${flightInfo.marketing?.flightDesignator?.carrierCode || carrierCode}${flightInfo.marketing?.flightDesignator?.flightNumber || flightNumber}`,
        departureIata: flightInfo.departure?.iataCode || "",
        arrivalIata: flightInfo.arrival?.iataCode || "",
        departureDateTime: flightInfo.departure?.localDateTime || "",
        arrivalDateTime: flightInfo.arrival?.localDateTime || "",
        departureAirportName: flightInfo.departureAirportLocation?.name || "",
        arrivalAirportName: flightInfo.arrivalAirportLocation?.name || "",
        departureCityName: flightInfo.departureAirportLocation?.address?.cityName || "",
        arrivalCityName: flightInfo.arrivalAirportLocation?.address?.cityName || "",
        departureTerminal: flightInfo.departure?.terminal || null,
        arrivalTerminal: flightInfo.arrival?.terminal || null,
        carrierCode: flightInfo.carrierCode || carrierCode,
        flightNumberOnly: flightInfo.number || flightNumber,
        airline: flightInfo.marketing?.carrier?.name || null
      }));
      
      console.debug('✅ Step 6: Processed Flight Options:', options);
      
      if (options.length > 1) {
        trackEvent('NewTrip', 'multiple_flight_options', `Count: ${options.length}`);
        setFlightOptions(options);
        setShowFlightDropdown(true);
        toast({
          title: t("common.toast.info"),
          description: t("Multiple flights found. Please select one.")
        });
      } else if (options.length === 1) {
        trackEvent('NewTrip', 'single_flight_option', options[0].flightNumber);
        addSelectedFlight(options[0]);
      }
      
    } catch (error) {
      console.error('❌ Error handling flight:', error);
      trackError('api', String(error), 'TripDetailsDrawer-AddFlight');
      toast({
        title: t("common.toast.error"),
        description: t("Failed to add flight: ") + error,
        variant: "destructive"
      });
    } finally {
      setIsAddingFlight(false);
    }
  };

  const addSelectedFlight = (option: FlightOption) => {
    const flightWithDetails: Flight = {
      flightNumber: option.flightNumber,
      departureDate: new Date(option.departureDateTime),
      arrivalDate: new Date(option.arrivalDateTime),
      departureTime: format(new Date(option.departureDateTime), "HH:mm"),
      arrivalTime: format(new Date(option.arrivalDateTime), "HH:mm"),
      departureIata: option.departureIata,
      arrivalIata: option.arrivalIata,
      details: {
        departure: {
          code: option.departureIata,
          time: option.departureDateTime,
          terminal: option.departureTerminal,
          airportName: option.departureAirportName,
          cityName: option.departureCityName
        },
        arrival: {
          code: option.arrivalIata,
          time: option.arrivalDateTime,
          terminal: option.arrivalTerminal,
          airportName: option.arrivalAirportName,
          cityName: option.arrivalCityName
        },
        airline: option.airline || option.carrierCode
      }
    };
    
    trackEvent('NewTrip', 'flight_added', `${option.flightNumber}: ${option.departureCityName} to ${option.arrivalCityName}`);
    setFlights([...flights, flightWithDetails]);
    setNewFlight({
      flightNumber: "",
      departureDate: new Date(),
      arrivalDate: new Date(),
      departureTime: "00:00",
      arrivalTime: "00:00",
      departureIata: "",
      arrivalIata: ""
    });
    flightForm.reset();
    setFlightOptions([]);
    setSelectedFlightIndex(null);
    
    toast({
      title: t("common.toast.success"),
      description: t("Flight added successfully")
    });
  };

  const handleAddFlightFromDropdown = () => {
    if (selectedFlightIndex !== null && flightOptions.length > selectedFlightIndex) {
      trackEvent('NewTrip', 'confirm_flight_selection', flightOptions[selectedFlightIndex].flightNumber);
      addSelectedFlight(flightOptions[selectedFlightIndex]);
    } else {
      trackEvent('NewTrip', 'validation_error', 'no_flight_selected');
      toast({
        title: t("common.toast.error"),
        description: t("Please select a flight option"),
        variant: "destructive"
      });
    }
  };

  const handleAddHotel = () => {
    if (!newHotel.name || !newHotel.city) {
      trackEvent('NewTrip', 'validation_error', 'incomplete_hotel_details');
      toast({
        title: t("common.toast.error"),
        description: t("Please fill in all hotel details"),
        variant: "destructive"
      });
      return;
    }
    
    const hotelToAdd = {
      ...newHotel,
      checkIn: newHotel.checkIn || today,
      checkOut: newHotel.checkOut || tomorrow
    };
    
    trackEvent('NewTrip', 'hotel_added', `${hotelToAdd.name} in ${hotelToAdd.city}, ${hotelToAdd.country}`);
    setHotels([...hotels, hotelToAdd]);
    
    setNewHotel({
      name: "",
      city: "",
      country: "",
      checkIn: today,
      checkOut: tomorrow
    });
    
    hotelForm.reset();
    
    setDateRange({
      from: today,
      to: tomorrow
    });
  };

  const handleManualEntry = async () => {
    trackEvent('NewTrip', 'create_trip_attempt', `Flights: ${flights.length}, Hotels: ${hotels.length}`);
    
    setIsCreatingTrip(true);
    
    try {
      const heyInstance = Hey.HeyUtils.getInstance();
      if (!heyInstance.isUserAuthenticated()) {
        toast({
          title: t("common.toast.error"),
          description: t("Please log in to create a trip"),
          variant: "destructive"
        });
        setIsCreatingTrip(false);
        return;
      }
      
      console.debug('Creating trip with flights:', flights);
      
      const adjustedHotels = hotels.map(hotel => adjustHotelTimesBasedOnFlights(hotel, flights));
      
      const products: Product[] = [...flights.map(flight => {
        const departureDateTime = flight.details?.departure?.time || `${format(flight.departureDate, "yyyy-MM-dd")}T${newFlight.departureTime}:00`;
        const arrivalDateTime = flight.details?.arrival?.time || `${format(flight.arrivalDate, "yyyy-MM-dd")}T${newFlight.arrivalTime}:00`;
        const flightNum = flight.flightNumber.replace(/\s+/g, '');
        const carrierCode = flightNum.substring(0, 2);
        const flightNumber = flightNum.substring(2).trim();
        return {
          air: {
            arrival: {
              iataCode: flight.details?.arrival?.code,
              localDateTime: arrivalDateTime,
              terminal: flight.details?.arrival?.terminal || "",
              baggageClaim: "",
              checkInEndTime: "",
              checkin: "",
              gate: "",
              localEstimatedDateTime: "",
              timeZone: ""
            },
            departure: {
              iataCode: flight.details?.departure?.code,
              localDateTime: departureDateTime,
              terminal: flight.details?.departure?.terminal || "",
              baggageClaim: "",
              checkInEndTime: "",
              checkin: "",
              gate: "",
              localEstimatedDateTime: "",
              timeZone: ""
            },
            departureAirportLocation: {
              airportAddress: {
                cityName: "",
                countryCode: "",
                state: ""
              },
              name: flight.details?.departure?.code,
              subtype: "AIRPORT"
            },
            arrivalAirportLocation: {
              airportAddress: {
                cityName: "",
                countryCode: "",
                state: ""
              },
              name: flight.details?.arrival?.code,
              subtype: "AIRPORT"
            },
            bookingClass: {
              cabin: {
                category: "ECONOMY"
              }
            },
            carrierCode: carrierCode,
            confirmationNumbers: [],
            connectionTime: "",
            marketing: {
              carrier:{name: flight.details?.airline},
              flightDesignator: {
                carrierCode: carrierCode,
                flightNumber: flightNumber,
                operationalSuffix: ""
              }
            },
            operating: {
              carrier:{name: flight.details?.airline},
              flightDesignator: {
                carrierCode: carrierCode,
                flightNumber: flightNumber,
                operationalSuffix: ""
              }
            },
            number: flightNumber,
            airlineRecloc: "",
            disruptionStatus: "",
            frequentFlyers: [],
            seat: {
              count: 1,
              number: "",
              row: "",
              size: ""
            },
            segmentStatus: "",
            suffix: ""
          }
        };
      }), ...adjustedHotels.map(hotel => {
        const countryCode = getCountryCodeByName(hotel.country || '');
        console.debug(`Creating hotel product with country: ${hotel.country}, code: ${countryCode}`);
        
        return {
          hotel: {
            name: hotel.name,
            address: {
              cityName: hotel.city,
              countryCode: countryCode,
              line: ""
            },
            chainCode: "",
            checkInDate: format(hotel.adjustedCheckIn || hotel.checkIn, "yyyy-MM-dd'T'HH:mm:ss"),
            checkOutDate: format(hotel.adjustedCheckOut || hotel.checkOut, "yyyy-MM-dd'T'HH:mm:ss"),
            cityCode: "",
            contact: {
              email: "",
              phone: ""
            },
            guests: {
              adults: travelers,
              childrenAges: []
            },
            latitude: 0,
            longitude: 0,
            media: "",
            rateCode: "",
            rating: "",
            room: {
              roomType: ""
            },
            roomQuantity: 1,
            timezone: "UTC"
          }
        };
      })];
      
      console.debug('Creating trip with products:', products);
      const heyTrip: HeyTrip = {
        numberOfPassengers: travelers,
        products: products,
        reference: `MAN${Date.now()}`,
        travelServiceLevel: 1
      };
      
      const trip = await heyInstance.addTripManually(heyTrip);
      if (!trip) {
        throw new Error("Failed to create trip");
      }
      await queryClient.invalidateQueries({
        queryKey: ['trips']
      });
      trackEvent('NewTrip', 'trip_created_success', tripTitle || 'Untitled Trip');
      trackIndexAction('add_new_trip', `MAN${Date.now()}`, `Method: Manual - Type: ${tripType}`);
      
      toast({
        title: t("common.toast.success"),
        description: t("Trip Created")
      });
      setIsCreatingTrip(false);
      setIsDrawerOpen(false);
      navigate("/");
    } catch (error) {
      console.error('Error creating trip:', error);
      trackError('api', String(error), 'TripDetailsDrawer-ManualEntry');
      toast({
        title: t("common.toast.error"),
        description: t("Failed to create trip: ") + error,
        variant: "destructive"
      });
      setIsCreatingTrip(false);
    }
  };

  const handleDateSelect = (date: Date | undefined, type: 'departure' | 'arrival') => {
    if (!date) return;
    if (type === 'departure') {
      setNewFlight(prev => ({
        ...prev,
        departureDate: date
      }));
      setCalendarOpen(prev => ({
        ...prev,
        flightDeparture: false
      }));
    } else {
      setNewFlight(prev => ({
        ...prev,
        arrivalDate: date
      }));
      setCalendarOpen(prev => ({
        ...prev,
        flightArrival: false
      }));
    }
    setIsDrawerOpen(true);
  };

  const handleHotelDateRangeSelect = (range: DateRange | undefined) => {
    if (!range) return;
    
    const validRange = {
      from: range.from || today,
      to: range.to || tomorrow
    };
    
    if (validRange.from && !range.to) {
      const nextDay = new Date(validRange.from);
      nextDay.setDate(nextDay.getDate() + 1);
      validRange.to = nextDay;
    }
    
    setDateRange(validRange);
    
    if (validRange.from && validRange.to) {
      setNewHotel(prev => ({
        ...prev,
        checkIn: validRange.from as Date,
        checkOut: validRange.to as Date
      }));
      
      setCalendarOpen(prev => ({
        ...prev,
        hotel: false
      }));
      setIsDrawerOpen(true);
    }
  };

  const handleIncreaseTravelers = () => {
    trackEvent('NewTrip', 'increase_travelers', `Count: ${travelers + 1}`);
    setTravelers(prev => Math.min(prev + 1, 10));
  };

  const handleDecreaseTravelers = () => {
    trackEvent('NewTrip', 'decrease_travelers', `Count: ${travelers - 1}`);
    setTravelers(prev => Math.max(prev - 1, 1));
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case 'info':
        return (
          <div className="space-y-5">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t("newTrip.drawer.tripInfo")}</h3>
              <Select value={tripType} onValueChange={onTripTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("newTrip.drawer.selectTripType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leisure">{t("newTrip.drawer.leisure")}</SelectItem>
                  <SelectItem value="business">{t("newTrip.drawer.business")}</SelectItem>
                  <SelectItem value="family">{t("newTrip.drawer.family")}</SelectItem>
                  <SelectItem value="bleisure">{t("newTrip.drawer.bleisure")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold flex items-center">
                <Users className="mr-2 h-5 w-5" />
                {t("newTrip.drawer.travelers")}
              </h3>
              <div className="flex items-center justify-between bg-muted p-3 rounded-lg">
                <Button variant="outline" size="icon" onClick={handleDecreaseTravelers} disabled={travelers <= 1}>
                  <UserMinus className="h-4 w-4" />
                </Button>
                <span className="text-lg font-medium">{travelers}</span>
                <Button variant="outline" size="icon" onClick={handleIncreaseTravelers} disabled={travelers >= 10}>
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      
      case 'flights':
        return (
          <div className="space-y-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold flex items-center mb-2">
                <Plane className="mr-2 h-5 w-5" />
                {t("newTrip.drawer.addFlight")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {flights.length === 0 
                  ? t("newTrip.drawer.flightsStepEmpty") 
                  : t("newTrip.drawer.flightsStepWithFlights", { count: flights.length })}
              </p>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <Input 
                placeholder={t("newTrip.drawer.flightNumber")} 
                value={newFlight.flightNumber} 
                onChange={e => {
                  const updatedFlight = {
                    ...newFlight,
                    flightNumber: e.target.value.toUpperCase()
                  };
                  setNewFlight(updatedFlight);
                }} 
                className="w-full" 
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("newTrip.drawer.departureDate")}</label>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-left font-normal" 
                  onClick={() => setCalendarOpen(prev => ({
                    ...prev,
                    flightDeparture: true
                  }))}
                >
                  <Plane className="mr-2 h-4 w-4 rotate-45" />
                  {format(newFlight.departureDate, "PPP")}
                </Button>
              </div>
              <Button 
                onClick={handleAddFlight} 
                className="w-full" 
                disabled={isAddingFlight} 
                style={{ backgroundColor: "#000835" }}
              >
                {isAddingFlight ? t("newTrip.drawer.adding") : t("newTrip.drawer.addFlight")}
              </Button>
            </div>

            {flightOptions.length > 0 && (
              <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                <h4 className="font-medium">{t("newTrip.drawer.selectFlight")}</h4>
                <div className="space-y-3">
                  {flightOptions.map((option, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg cursor-pointer ${selectedFlightIndex === index ? 'bg-primary/10 border border-primary' : 'bg-background'}`}
                      onClick={() => selectFlightOption(index)}
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{option.flightNumber}</span>
                        <span>{option.airline || option.carrierCode}</span>
                      </div>
                      <div className="mt-2 text-sm">
                        <div>
                          {option.departureCityName} ({option.departureIata}) → {option.arrivalCityName} ({option.arrivalIata})
                        </div>
                        <div className="text-muted-foreground mt-1">
                          {formatDateTime(option.departureDateTime)} → {formatDateTime(option.arrivalDateTime)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  onClick={handleAddFlightFromDropdown} 
                  className="w-full mt-2" 
                  disabled={selectedFlightIndex === null}
                  style={{ backgroundColor: "#000835" }}
                >
                  {t("newTrip.drawer.confirmFlight")}
                </Button>
              </div>
            )}

            {flights.length > 0 && (
              <div className="mt-3 space-y-2">
                <h4 className="font-medium">{t("newTrip.drawer.addedFlights")} ({flights.length})</h4>
                <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                  {flights.map((flight, index) => (
                    <div key={index} className="bg-muted p-3 rounded space-y-2 relative">
                      <button 
                        onClick={() => {
                          const newFlights = [...flights];
                          newFlights.splice(index, 1);
                          setFlights(newFlights);
                        }} 
                        className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full transition-colors" 
                        aria-label="Delete flight"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{flight.flightNumber}</span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="font-medium">{t("newTrip.drawer.from")}</div>
                        <div className="pl-2">
                          <div>{flight.details?.departure?.airportName || flight.departureIata}</div>
                          <div>{flight.details?.departure?.cityName || 'N/A'}</div>
                          <div>{t("newTrip.drawer.terminal")} {flight.details?.departure?.terminal || 'N/A'}</div>
                          <div>{t("newTrip.drawer.departure")} {flight.details?.departure?.time ? format(new Date(flight.details?.departure?.time), "PPP 'at' p") : format(new Date(flight.departureDate), "PPP 'at' p")}</div>
                        </div>
                        <div className="font-medium mt-2">{t("newTrip.drawer.to")}</div>
                        <div className="pl-2">
                          <div>{flight.details?.arrival?.airportName || flight.arrivalIata}</div>
                          <div>{flight.details?.arrival?.cityName || 'N/A'}</div>
                          <div>{t("newTrip.drawer.terminal")} {flight.details?.arrival?.terminal || 'N/A'}</div>
                          <div>{t("newTrip.drawer.arrival")} {flight.details?.arrival?.time ? format(new Date(flight.details?.arrival?.time), "PPP 'at' p") : format(new Date(flight.arrivalDate), "PPP 'at' p")}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'hotels':
        return (
          <div className="space-y-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold flex items-center mb-2">
                <HotelIcon className="mr-2 h-5 w-5" />
                {t("newTrip.drawer.addHotel")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hotels.length === 0 
                  ? t("newTrip.drawer.hotelsStepEmpty") 
                  : t("newTrip.drawer.hotelsStepWithHotels", { count: hotels.length })}
              </p>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <Input 
                placeholder={t("newTrip.drawer.hotelName")} 
                value={newHotel.name} 
                onChange={e => {
                  const updatedHotel = {
                    ...newHotel,
                    name: e.target.value
                  };
                  setNewHotel(updatedHotel);
                }} 
                className="w-full" 
              />
              
              <LocationFields
                city={newHotel.city || ""}
                country={newHotel.country || ""}
                onCityChange={(value) => {
                  setNewHotel(prev => ({
                    ...prev,
                    city: value
                  }));
                }}
                onCountryChange={(value) => {
                  setNewHotel(prev => ({
                    ...prev,
                    country: value,
                    city: ""
                  }));
                }}
              />
              
              <Button 
                variant="outline" 
                className="w-full justify-start text-left font-normal" 
                onClick={() => setCalendarOpen(prev => ({
                  ...prev,
                  hotel: true
                }))}
              >
                <HotelIcon className="mr-2 h-4 w-4" />
                {dateRange.from 
                  ? dateRange.to 
                    ? <>{format(dateRange.from, "PPP")} - {format(dateRange.to, "PPP")}</>
                    : format(dateRange.from, "PPP") 
                  : t("newTrip.drawer.selectCheckInOut")}
              </Button>
              <Button 
                onClick={handleAddHotel} 
                className="w-full" 
                style={{ backgroundColor: "#000835" }}
              >
                {t("newTrip.drawer.addHotel")}
              </Button>
            </div>

            {hotels.length > 0 && (
              <div className="mt-3 space-y-2">
                <h4 className="font-medium">{t("newTrip.drawer.addedHotels")} ({hotels.length})</h4>
                <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                  {hotels.map((hotel, index) => (
                    <div key={index} className="bg-muted p-3 rounded space-y-2 relative">
                      <button 
                        onClick={() => {
                          const newHotels = [...hotels];
                          newHotels.splice(index, 1);
                          setHotels(newHotels);
                        }} 
                        className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full transition-colors" 
                        aria-label="Delete hotel"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{hotel.name}</span>
                      </div>
                      <div className="text-sm">
                        {hotel.city}{hotel.country ? `, ${hotel.country}` : ''}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {format(hotel.checkIn, "PPP")} - {format(hotel.checkOut, "PPP")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'review':
        return (
          <div className="space-y-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">{t("newTrip.drawer.reviewTrip")}</h3>
              <p className="text-sm text-muted-foreground">{t("newTrip.drawer.reviewDescription")}</p>
            </div>

            <div className="space-y-5">
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">{t("newTrip.drawer.tripSummary")}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("newTrip.drawer.tripName")}:</span>
                    <span>{tripTitle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("newTrip.drawer.tripType")}:</span>
                    <span>{t(`newTrip.drawer.${tripType}`)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("newTrip.drawer.travelers")}:</span>
                    <span>{travelers}</span>
                  </div>
                </div>
              </div>

              {flights.length > 0 && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">{t("newTrip.drawer.flightsSummary")} ({flights.length})</h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {flights.map((flight, index) => (
                      <div key={index} className="p-2 bg-background rounded-md">
                        <div className="font-medium">{flight.flightNumber}</div>
                        <div className="text-sm">
                          {flight.details?.departure?.cityName || flight.departureIata} → {flight.details?.arrival?.cityName || flight.arrivalIata}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {flight.details?.departure?.time 
                            ? format(new Date(flight.details?.departure?.time), "PPP") 
                            : format(flight.departureDate, "PPP")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {hotels.length > 0 && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">{t("newTrip.drawer.hotelsSummary")} ({hotels.length})</h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {hotels.map((hotel, index) => (
                      <div key={index} className="p-2 bg-background rounded-md">
                        <div className="font-medium">{hotel.name}</div>
                        <div className="text-sm">{hotel.city}{hotel.country ? `, ${hotel.country}` : ''}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(hotel.checkIn, "PPP")} - {format(hotel.checkOut, "PPP")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(flights.length > 0 || hotels.length > 0) && (
                <TripTimeline flights={flights} hotels={hotels} />
              )}
            </div>
          </div>
        );
    }
  };

  const renderFooterButtons = () => {
    switch(currentStep) {
      case 'info':
        return (
          <Button 
            onClick={goToNextStep}
            className="w-full" 
            style={{ backgroundColor: "#000835" }}
          >
            {t("newTrip.drawer.next")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        );
      case 'flights':
        return (
          <div className="flex space-x-2">
            <Button 
              onClick={goToPreviousStep}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("newTrip.drawer.back")}
            </Button>
            <Button 
              onClick={goToNextStep}
              className="flex-1" 
              style={{ backgroundColor: "#000835" }}
              disabled={flights.length === 0}
            >
              {t("newTrip.drawer.next")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      case 'hotels':
        return (
          <div className="flex space-x-2">
            <Button 
              onClick={goToPreviousStep}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("newTrip.drawer.back")}
            </Button>
            <Button 
              onClick={goToNextStep}
              className="flex-1" 
              style={{ backgroundColor: "#000835" }}
            >
              {t("newTrip.drawer.review")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      case 'review':
        return (
          <div className="flex space-x-2">
            <Button 
              onClick={goToPreviousStep}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("newTrip.drawer.back")}
            </Button>
            <Button 
              onClick={handleManualEntry}
              className="flex-1" 
              disabled={(flights.length === 0 && hotels.length === 0) || isCreatingTrip}
              style={{ backgroundColor: "#000835" }}
            >
              {isCreatingTrip ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("newTrip.drawer.creatingTrip")}
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  {t("newTrip.drawer.createTrip")}
                </>
              )}
            </Button>
          </div>
        );
    }
  };

  return (
    <>
      
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t("newTrip.manual")}</CardTitle>
            <CardDescription>{t("newTrip.manualEntryDesc")}</CardDescription>
            <div className="pt-2">
              <Progress value={workflowProgress} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span className={currentStep === 'info' ? 'font-medium text-primary' : ''}>
                  {t("newTrip.drawer.step1")}
                </span>
                <span className={currentStep === 'flights' ? 'font-medium text-primary' : ''}>
                  {t("newTrip.drawer.step2")}
                </span>
                <span className={currentStep === 'hotels' ? 'font-medium text-primary' : ''}>
                  {t("newTrip.drawer.step3")}
                </span>
                <span className={currentStep === 'review' ? 'font-medium text-primary' : ''}>
                  {t("newTrip.drawer.step4")}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 space-y-5 overflow-y-auto hide-scrollbar max-h-[70vh]">
              {renderStepContent()}
            </div>
            <div className="p-4 mt-4">
              {renderFooterButtons()}
            </div>
          </CardContent>
        </Card>
      

      <Dialog open={calendarOpen.flightDeparture} onOpenChange={open => {
        setCalendarOpen(prev => ({
          ...prev,
          flightDeparture: open
        }));
        if (!open) setIsDrawerOpen(true);
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("newTrip.drawer.departureDate")}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <Calendar mode="single" selected={newFlight.departureDate} onSelect={date => handleDateSelect(date, 'departure')} className="rounded-md border" />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={calendarOpen.flightArrival} onOpenChange={open => {
        setCalendarOpen(prev => ({
          ...prev,
          flightArrival: open
        }));
        if (!open) setIsDrawerOpen(true);
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("tripCard.arrival")}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <Calendar mode="single" selected={newFlight.arrivalDate} onSelect={date => handleDateSelect(date, 'arrival')} className="rounded-md border" />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={calendarOpen.hotel} onOpenChange={open => {
        setCalendarOpen(prev => ({
          ...prev,
          hotel: open
        }));
        if (!open && dateRange.from) {
          setIsDrawerOpen(true);
        }
      }}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{t("newTrip.drawer.selectCheckInOut")}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <Calendar 
              mode="range" 
              selected={dateRange} 
              onSelect={handleHotelDateRangeSelect} 
              numberOfMonths={2} 
              className="rounded-md border" 
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
